import { BottomNavigation, BottomNavigationAction } from "@mui/material";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { useIntl } from "react-intl";

import {
  IconBookings,
  IconBookingsActive,
  IconChat,
  IconChatActive,
  IconHome,
  IconHomeActive,
  IconTravellers,
  IconTravellersActive,
} from "@/components/Icons";
import { CommonTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";

const ROUTES = {
  search: "/",
  bookings: "/bookings",
  travelers: "/travelers",
  chat: "/chat",
} as const;

const eventMap = {
  search: CommonTrackingEvents.USER_PRESSED_SEARCH_NAVIGATION_BUTTON,
  travelers: CommonTrackingEvents.USER_PRESSED_TRAVELERS_NAVIGATION_BUTTON,
  bookings: CommonTrackingEvents.USER_PRESSED_MY_TRIPS_NAVIGATION_BUTTON,
  chat: CommonTrackingEvents.USER_PRESSED_CHAT_NAVIGATION_BUTTON,
} as const;

export const Footer = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const matches = useMatchRoute();

  const handleChange = (
    _: React.SyntheticEvent,
    newValue: keyof typeof ROUTES,
  ) => {
    rudderAnalytics.track(eventMap[newValue]);

    navigate({
      to: ROUTES[newValue],
    });
  };

  const getCurrentTabValue = (): keyof typeof ROUTES | "" => {
    const routes = [
      { route: ROUTES.bookings, value: "bookings" },
      { route: ROUTES.travelers, value: "travelers" },
      { route: ROUTES.chat, value: "chat" },
      { route: ROUTES.search, value: "search" },
    ] as const;

    for (const { route, value } of routes) {
      if (matches({ to: route, fuzzy: true })) {
        return value;
      }
    }

    return "";
  };

  const currentTabValue = getCurrentTabValue();

  return (
    <BottomNavigation
      className="fixed bottom-0 z-20 w-full max-w-xl flex-row rounded-t-2xl border-t border-t-slate-300 pt-2 pb-2 text-xs"
      showLabels
      value={currentTabValue}
      onChange={handleChange}
      sx={{
        height: "auto",
        paddingBottom: "calc(env(safe-area-inset-bottom) + var(--spacing) * 2)",
      }}
    >
      <BottomNavigationAction
        label={intl.formatMessage({
          defaultMessage: "Home",
          description:
            "Bottom navigation tab label for the Search / Home screen",
        })}
        icon={currentTabValue === "search" ? <IconHomeActive /> : <IconHome />}
        value="search"
      />
      <BottomNavigationAction
        label={intl.formatMessage({
          defaultMessage: "My Bookings",
          description:
            "Page title and bottom navigation label for the My Bookings screen",
        })}
        icon={
          currentTabValue === "bookings" ? (
            <IconBookingsActive />
          ) : (
            <IconBookings />
          )
        }
        value="bookings"
      />
      <BottomNavigationAction
        label={intl.formatMessage({
          defaultMessage: "Travellers",
          description:
            "Page title for the traveller management / saved profiles screen",
        })}
        icon={
          currentTabValue === "travelers" ? (
            <IconTravellersActive />
          ) : (
            <IconTravellers />
          )
        }
        value="travelers"
      />
      <BottomNavigationAction
        label={intl.formatMessage({
          defaultMessage: "Chat",
          description: "Navigation tab label for the Chat / Support screen",
        })}
        icon={currentTabValue === "chat" ? <IconChatActive /> : <IconChat />}
        value="chat"
      />
    </BottomNavigation>
  );
};
